import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import dayjs from "dayjs";
import { Button, Modal, Spin } from "antd";
import { getWorkItemThread } from "../../../service/workItems";
import { sanitizeRichHtml } from "../../../utils/sanitizeRichHtml";
import { splitMatterLabel } from "../../../utils/matterLabel";
import {
  border,
  spacing,
  text,
  typography,
} from "../../../theme/tokens";

const Meta = styled.p`
  margin: 0 0 ${spacing[3]};
  font-size: ${typography.size.sm};
  color: ${text.muted};
`;

const SectionLabel = styled.h4`
  margin: ${spacing[3]} 0 ${spacing[1]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${text.muted};
`;

const Ask = styled.div`
  font-size: ${typography.size.base};
  color: ${text.primary};
  line-height: 1.45;

  p {
    margin: 0 0 ${spacing[2]};
  }

  a {
    color: inherit;
    text-decoration: underline;
  }
`;

const Exchange = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Beat = styled.li`
  padding: ${spacing[2]} 0;
  border-top: 1px solid ${border.light};

  &:first-child {
    border-top: none;
  }
`;

const BeatWho = styled.div`
  font-size: ${typography.size.sm};
  color: ${text.muted};
  margin-bottom: ${spacing[1]};
`;

const BeatBody = styled.div`
  font-size: ${typography.size.base};
  color: ${text.primary};
  line-height: 1.45;

  p {
    margin: 0;
  }
`;

const Quiet = styled.p`
  margin: ${spacing[2]} 0 0;
  font-size: ${typography.size.sm};
  color: ${text.muted};
`;

const when = (value) => (value ? dayjs(value).format("M/D") : "");

/**
 * The finished row only quotes the last note. Opening it shows the ask
 * and every note on the item, on this page — not the matter.
 */
const FinishedWorkModal = ({ row, onClose, onAcknowledge, readOnly }) => {
  const [entries, setEntries] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setEntries(null);
    setFailed(false);
    getWorkItemThread(row.id)
      .then((response) => {
        if (cancelled) return;
        const list = response?.data?.entries;
        setEntries(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
        setEntries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [row.id]);

  const matter = splitMatterLabel(row.matter_name);
  const asked = [row.requested_by, when(row.sent_at)].filter(Boolean).join(" · ");
  const closed = [row.closed_by && `closed by ${row.closed_by}`, when(row.closed_at)]
    .filter(Boolean)
    .join(" ");
  const meta = [row.item_number, matter.short || matter.full, asked && `asked by ${asked}`, closed]
    .filter(Boolean)
    .join(" · ");

  const acknowledge = () => {
    onAcknowledge?.(row);
    onClose();
  };

  return (
    <Modal
      open
      title={row.title || "Work item"}
      onCancel={onClose}
      width={640}
      destroyOnHidden
      footer={
        readOnly || !onAcknowledge
          ? null
          : [
              <Button key="close" onClick={onClose}>
                Close
              </Button>,
              <Button key="ack" type="primary" onClick={acknowledge}>
                Acknowledge
              </Button>,
            ]
      }
    >
      {meta && <Meta>{meta}</Meta>}

      {row.instruction && (
        <>
          <SectionLabel>What was asked</SectionLabel>
          <Ask
            data-testid="finished-ask"
            dangerouslySetInnerHTML={{
              __html: sanitizeRichHtml(row.instruction),
            }}
          />
        </>
      )}

      <SectionLabel>The exchange</SectionLabel>
      {entries === null ? (
        <Spin size="small" />
      ) : (
        <>
          {failed && (
            <Quiet role="alert">
              Couldn&apos;t load the rest of what was said.
            </Quiet>
          )}
          {entries.length > 0 ? (
            <Exchange data-testid="finished-exchange">
              {entries.map((entry, index) => (
                <Beat key={`${entry.createdAt || entry.whenLabel || index}`}>
                  <BeatWho>
                    {[entry.author, entry.whenLabel, entry.stepLabel]
                      .filter(Boolean)
                      .join(" · ")}
                  </BeatWho>
                  {entry.body && (
                    <BeatBody
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichHtml(entry.body),
                      }}
                    />
                  )}
                </Beat>
              ))}
            </Exchange>
          ) : (
            row.note && <BeatBody>{row.note}</BeatBody>
          )}
          {!failed && entries.length === 0 && !row.note && (
            <Quiet>Nothing was written on this one.</Quiet>
          )}
        </>
      )}
    </Modal>
  );
};

FinishedWorkModal.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    item_number: PropTypes.string,
    matter_name: PropTypes.string,
    instruction: PropTypes.string,
    note: PropTypes.string,
    requested_by: PropTypes.string,
    sent_at: PropTypes.string,
    closed_by: PropTypes.string,
    closed_at: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onAcknowledge: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default FinishedWorkModal;
